import api from './client';
import type { SignatureRequest } from 'types';

export type SignaturePlacementMode = "appended_page" | "inline_fields";

export interface InlineSignatureField {
  type: "Signature" | "Text" | "Date" | "Tampon";
  x: number;
  y: number;
  width: number;
  height: number;
  page: number;
  label?: string;
  content?: string;
  /** Inline mode: id of the signer who owns this field. */
  signer_id?: string;
}

export interface CreateSignatureRequestPayload {
  project_id: string;
  org_id: string;
  document_name: string;
  file_id: string;
  signers: {
    id: string;
    signer_name: string;
    signer_email: string;
    signer_type: string;
    /** Optional initials to paint next to the date on the signed PDF. */
    initials?: string;
  }[];
  /**
   * Placement mode governs how signatures are composited onto the PDF.
   * "appended_page" (default) keeps the legacy recap-page flow; pass
   * "inline_fields" together with `fields` + canvas dimensions to drop
   * signature fields directly inside the document.
   */
  placement_mode?: SignaturePlacementMode;
  fields?: InlineSignatureField[];
  target_width?: number;
  target_height?: number;
}

export const signatureRequestsApi = {
  create: async (payload: CreateSignatureRequestPayload): Promise<SignatureRequest> => {
    const { data } = await api.post('/signature-requests', payload);
    return data;
  },

  listForProject: async (projectId: string): Promise<SignatureRequest[]> => {
    const { data } = await api.get(`/signature-requests/project/${projectId}`);
    return data;
  },

  listPendingForMe: async (): Promise<SignatureRequest[]> => {
    const { data } = await api.get('/signature-requests/pending-for-me');
    return data;
  },

  get: async (requestId: string): Promise<SignatureRequest> => {
    const { data } = await api.get(`/signature-requests/${requestId}`);
    return data;
  },

  resend: async (requestId: string, signerId: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post(`/signature-requests/${requestId}/resend`, { signer_id: signerId });
    return data;
  },

  submitSignature: async (token: string, signatureFile: File | Blob): Promise<{ success: boolean; message: string }> => {
    const formData = new FormData();
    formData.append('signature', signatureFile);
    const { data } = await api.post(`/signature-requests/sign/${token}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};
